import {OfferNode} from '../types'

/** Aggregated headline figures for the whole rendered offers tree. */
export type NetworkStats = {
  /** Combined deposits across every offer in the tree. */
  networkAum: number
  /** Combined investor count across every offer in the tree. */
  totalInvestors: number
  /** Number of agents below the rendered root(s) — i.e. every non-root node. */
  subAgentCount: number
  /**
   * Combined performance fee collected across the tree over the trailing 30 days (PT-5717).
   *
   * Every node reports the fee its own offer collected, so summing the tree gives what the network
   * produced in total — for a Money Manager that is the whole downline's contribution flowing up to
   * them, and in the agent view it is the same figure for the agent's own subtree.
   */
  pfCollected: number
}

/** Flattens the offers tree into a depth-first list of every node it contains. */
export const flattenTree = (roots: OfferNode[]): OfferNode[] => {
  const flat: OfferNode[] = []
  const visit = (node: OfferNode): void => {
    flat.push(node)
    node.children.forEach(visit)
  }
  roots.forEach(visit)
  return flat
}

/**
 * Computes the headline KPI figures shown above the tree (Network AUM, Total Investors,
 * Sub-Agents, PF Collected) by summing the relevant fields across every offer in the hierarchy.
 *
 * @param roots - Root nodes produced by `buildOfferTree`.
 */
export const aggregateNetworkStats = (roots: OfferNode[]): NetworkStats => {
  const allNodes = flattenTree(roots)
  return {
    networkAum: allNodes.reduce((sum, node) => sum + (node.offer.totalDepositSum ?? 0), 0),
    totalInvestors: allNodes.reduce((sum, node) => sum + (node.offer.totalInvestments ?? 0), 0),
    subAgentCount: allNodes.length - roots.length,
    pfCollected: allNodes.reduce((sum, node) => sum + (node.offer.pfCollected30d ?? 0), 0),
  }
}
